import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import type { TrackingEntry } from "./DataTrackingList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DataTrackingFormValues {
  category: string;
  label: string;
  value: string;
  textValue: string;
  measurementDate: string;
  notes: string;
}

interface DataTrackingFormProps {
  personId: string;
  entry?: TrackingEntry;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataTrackingForm({
  personId,
  entry,
  onClose,
}: DataTrackingFormProps) {
  const queryClient = useQueryClient();
  const isEditing = !!entry;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DataTrackingFormValues>({
    defaultValues: {
      category: entry?.category ?? "",
      label: entry?.label ?? "",
      value: entry?.value !== null && entry?.value !== undefined ? String(entry.value) : "",
      textValue: entry?.text_value ?? "",
      measurementDate:
        entry?.measurement_date ?? new Date().toISOString().split("T")[0],
      notes: entry?.notes ?? "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: DataTrackingFormValues) =>
      api.post(`/clients/${personId}/tracking`, {
        category: data.category,
        label: data.label,
        value: data.value ? parseFloat(data.value) : undefined,
        textValue: data.textValue || undefined,
        measurementDate: data.measurementDate,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracking", personId] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: DataTrackingFormValues) =>
      api.put(`/clients/${personId}/tracking/${entry!.id}`, {
        category: data.category,
        label: data.label,
        value: data.value ? parseFloat(data.value) : undefined,
        textValue: data.textValue || undefined,
        measurementDate: data.measurementDate,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracking", personId] });
      onClose();
    },
  });

  const mutation = isEditing ? updateMutation : createMutation;

  function onSubmit(data: DataTrackingFormValues) {
    mutation.mutate(data);
  }

  return (
    <Card className="border-primary/30">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">
          {isEditing ? "Edit Tracking Entry" : "New Tracking Entry"}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Category"
              placeholder="e.g., Mental Health, Housing, Safety"
              required
              error={errors.category?.message}
              {...register("category", {
                required: "Category is required",
              })}
            />
            <Input
              label="Label"
              placeholder="e.g., PHQ-9 Score, Safety Rating"
              required
              error={errors.label?.message}
              {...register("label", {
                required: "Label is required",
              })}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label="Numeric Value"
              type="number"
              step="any"
              placeholder="e.g., 12"
              {...register("value")}
            />
            <Input
              label="Text Value"
              placeholder="e.g., Improved"
              {...register("textValue")}
            />
            <Input
              label="Measurement Date"
              type="date"
              required
              error={errors.measurementDate?.message}
              {...register("measurementDate", {
                required: "Date is required",
              })}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium leading-none text-foreground">
              Notes
            </label>
            <textarea
              {...register("notes")}
              rows={2}
              placeholder="Additional notes..."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Error */}
          {mutation.isError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {(mutation.error as Error)?.message ?? "An error occurred"}
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" size="sm" loading={mutation.isPending}>
              {isEditing ? "Save" : "Add Entry"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
